import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/common/Page";
import { Spinner } from "@/components/common/States";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import AuthShell from "@/pages/auth/AuthShell";

const homeFor = (u) => (u.role === "admin" ? "/admin" : u.role === "student" ? "/portal" : u.onboarding_completed ? "/dashboard" : "/onboarding");

const useSubmit = () => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run };
};

const ErrorText = ({ error, testId }) => (error ? <p className="text-sm text-destructive" data-testid={testId}>{error}</p> : null);

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const { busy, error, run } = useSubmit();
  const submit = (e) => {
    e.preventDefault();
    run(async () => navigate(homeFor(await login(form.email, form.password))));
  };
  return (
    <AuthShell title="Bem-vindo de volta" subtitle="Entre para organizar suas aulas.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Field label="E-mail"><Input type="email" autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="login-email-input" /></Field>
        <Field label="Senha"><Input type="password" autoComplete="current-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="login-password-input" /></Field>
        <div className="text-right"><Link to="/esqueci-senha" className="text-sm font-medium text-primary hover:underline" data-testid="forgot-password-link">Esqueci minha senha</Link></div>
        <ErrorText error={error} testId="login-error" />
        <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit-btn">{busy && <Spinner className="mr-2" />}Entrar</Button>
        <p className="text-center text-sm text-muted-foreground">Ainda não tem conta? <Link to="/cadastro" className="font-semibold text-primary hover:underline" data-testid="register-link">Criar conta grátis</Link></p>
      </form>
    </AuthShell>
  );
}

export function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", password: "", confirm_password: "" });
  const { busy, error, run } = useSubmit();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = (e) => {
    e.preventDefault();
    run(async () => {
      await register(form);
      toast.success("Conta criada! Enviamos um e-mail de confirmação.");
      navigate("/onboarding");
    });
  };
  return (
    <AuthShell title="Crie sua conta" subtitle="Comece grátis com até 5 alunos. Sem cartão de crédito.">
      <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" data-testid="register-form">
        <Field label="Nome"><Input value={form.first_name} onChange={set("first_name")} required data-testid="register-first-name-input" /></Field>
        <Field label="Sobrenome"><Input value={form.last_name} onChange={set("last_name")} required data-testid="register-last-name-input" /></Field>
        <Field label="E-mail" className="sm:col-span-2"><Input type="email" value={form.email} onChange={set("email")} required data-testid="register-email-input" /></Field>
        <Field label="Telefone" className="sm:col-span-2"><Input placeholder="(11) 99999-9999" value={form.phone} onChange={set("phone")} required data-testid="register-phone-input" /></Field>
        <Field label="Senha" hint="Mínimo 8 caracteres, com letras e números"><Input type="password" value={form.password} onChange={set("password")} required data-testid="register-password-input" /></Field>
        <Field label="Confirmar senha"><Input type="password" value={form.confirm_password} onChange={set("confirm_password")} required data-testid="register-confirm-password-input" /></Field>
        <div className="sm:col-span-2 space-y-4">
          <ErrorText error={error} testId="register-error" />
          <Button type="submit" className="w-full" disabled={busy} data-testid="register-submit-btn">{busy && <Spinner className="mr-2" />}Criar conta</Button>
          <p className="text-center text-xs text-muted-foreground">Ao criar a conta você concorda com o tratamento dos seus dados conforme a LGPD.</p>
          <p className="text-center text-sm text-muted-foreground">Já tem conta? <Link to="/login" className="font-semibold text-primary hover:underline" data-testid="login-link">Entrar</Link></p>
        </div>
      </form>
    </AuthShell>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState("");
  const { busy, error, run } = useSubmit();
  const submit = (e) => {
    e.preventDefault();
    run(async () => setSent((await api.post("/auth/forgot-password", { email })).data.message));
  };
  return (
    <AuthShell title="Recuperar senha" subtitle="Enviaremos um link para você criar uma nova senha.">
      {sent ? (
        <div className="rounded-xl border bg-card p-5 text-sm" data-testid="forgot-password-success">{sent}</div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Field label="E-mail"><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email-input" /></Field>
          <ErrorText error={error} testId="forgot-error" />
          <Button type="submit" className="w-full" disabled={busy} data-testid="forgot-submit-btn">{busy && <Spinner className="mr-2" />}Enviar link</Button>
        </form>
      )}
      <p className="mt-6 text-center text-sm"><Link to="/login" className="font-semibold text-primary hover:underline" data-testid="back-to-login-link">Voltar para o login</Link></p>
    </AuthShell>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({ password: "", confirm: "" });
  const { busy, error, run } = useSubmit();
  const submit = (e) => {
    e.preventDefault();
    run(async () => {
      if (form.password !== form.confirm) throw { response: { data: { detail: "As senhas não conferem" } } };
      await api.post("/auth/reset-password", { token: params.get("token") || "", password: form.password });
      toast.success("Senha redefinida! Faça login.");
      navigate("/login");
    });
  };
  return (
    <AuthShell title="Nova senha" subtitle="Escolha uma senha forte para sua conta.">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Nova senha" hint="Mínimo 8 caracteres, com letras e números"><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="reset-password-input" /></Field>
        <Field label="Confirmar nova senha"><Input type="password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} required data-testid="reset-confirm-input" /></Field>
        <ErrorText error={error} testId="reset-error" />
        <Button type="submit" className="w-full" disabled={busy} data-testid="reset-submit-btn">{busy && <Spinner className="mr-2" />}Redefinir senha</Button>
      </form>
    </AuthShell>
  );
}

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const { user, refresh } = useAuth();
  const [state, setState] = useState({ status: "loading", message: "" });
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    api.post("/auth/verify-email", { token: params.get("token") || "" })
      .then(({ data }) => { setState({ status: "ok", message: data.message }); refresh(); })
      .catch((e) => setState({ status: "error", message: errMsg(e) }));
  }, [params, refresh]);
  return (
    <AuthShell title="Verificação de e-mail">
      <div className="rounded-xl border bg-card p-6 text-center" data-testid={`verify-email-${state.status}`}>
        {state.status === "loading" ? <Spinner className="mx-auto h-6 w-6" /> : state.status === "ok" ? <CheckCircle2 className="mx-auto h-10 w-10 text-accent" /> : <XCircle className="mx-auto h-10 w-10 text-destructive" />}
        <p className="mt-3 text-sm">{state.message || "Verificando..."}</p>
        <Button asChild className="mt-5" data-testid="verify-continue-btn"><Link to={user ? homeFor(user) : "/login"}>Continuar</Link></Button>
      </div>
    </AuthShell>
  );
}
